import classnames from 'classnames';

import { useTranslator } from '@picsart/rc/services/localization';

import Spinner from '@/components/Spinner';
import { useAppStore, type IAppStore } from '@/store';

import useStyles from './styles';
import type { GenerationStatusBadgeProps } from './types';

const selector = (store: IAppStore) => ({
  contentFiles: store.contentFiles,
  pendingReadyCount: store.pendingReadyCount,
});

/**
 * Decorates an existing creations entry point — the mobile Creations icon, the
 * portfolio avatar, the desktop tab. Generations poll in a detached promise
 * chain, so reading the store is enough to follow one from any screen.
 *
 */
const GenerationStatusBadge = ({ isInline }: GenerationStatusBadgeProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { contentFiles, pendingReadyCount } = useAppStore(selector);

  const isGenerating = contentFiles.some(({ isLoading }) => isLoading);

  if (!isGenerating && pendingReadyCount === 0) return null;

  return (
    <span
      className={classnames(classes.badge, {
        [classes.inline]: isInline,
        [classes.overlay]: !isInline && isGenerating,
        [classes.corner]: !isInline && !isGenerating,
      })}
      role="status"
      aria-label={translate(
        isGenerating ? 'Generating...' : 'Your creation is ready!'
      )}
      data-testid="generation-status-badge"
      data-state={isGenerating ? 'generating' : 'ready'}
    >
      {isGenerating ? (
        isInline ? (
          <Spinner size="xsmall" />
        ) : (
          <span className={classes.ring} data-testid="generation-status-ring" />
        )
      ) : (
        <span className={classes.dot} data-testid="generation-status-dot" />
      )}
    </span>
  );
};

export default GenerationStatusBadge;
